import { useState, useRef, ReactNode, HTMLAttributes } from 'react';
import styled from 'styled-components';
import { nanoid } from 'nanoid';
import Image from 'next/legacy/image';

import { colors, effects } from '/css';
import Description from '/components/ui/typography/Description';
import Divider from '/components/ui/Divider';
import Button from './buttons/Button';
import Icon from '/components/ui/Icon';
import { QuestionType } from '/types/jobTemplateTypes';

interface Props extends HTMLAttributes<HTMLInputElement> {
    type?:
        | QuestionType.CHECKBOX
        | QuestionType.RADIO
        | QuestionType.PRODUCT_SELECTOR;
    name?: string;
    onChange: (val: any) => void;
    onClick?: (val: any) => void;
    value: any;
    checked: boolean;
    label: string;
    description?: ReactNode;
    expandableDetails?: ReactNode;
    expandableDetailsLabel?: ReactNode;
    image?: string;
    imageAlt?: string;
    labelImage?: ReactNode;
    disabled?: boolean;
    suffix?: ReactNode;
    suffixZIndex?: number;
    zIndex?: number;
    theme?: string;
    onReadMore?: () => void;
    alternativeChoiceLayout?: boolean;
    statusLabel?: ReactNode;
}

const Radio = ({
    type = QuestionType.RADIO,
    className = '',
    name = '',
    onChange = (val) => {},
    onClick = undefined,
    value = '',
    checked = false,
    label,
    description,
    expandableDetails,
    expandableDetailsLabel,
    style = {},
    image = '',
    imageAlt = '',
    labelImage = '',
    disabled = false,
    suffix = '',
    suffixZIndex,
    zIndex = 1,
    theme = '',
    onReadMore = () => {},
    alternativeChoiceLayout = false,
    statusLabel = null,
}: Props) => {
    const id = ((name || nanoid(9)) + '-' + String(value)).replace(/\s/g, '-');

    const [detailsExpanded, setDetailsExpanded] = useState(false);

    const expandableDetailsToggleRef = useRef<HTMLDivElement>(null);

    const hasDescription = () => {
        if (typeof description === 'string') return description;

        if (typeof description === 'object') {
            //@ts-ignore
            return (description?.props?.children || []).some((c) => c);
        }

        return false;
    };

    return (
        <Wrapper
            className={[
                'radio-button',
                `type-${type || 'radio'}`,
                checked ? 'checked' : null,
                disabled ? 'disabled' : null,
                id,
                className || '',
                `theme-${theme || ''}`,
            ].join(' ')}
            style={style}
            zIndex={zIndex}
        >
            <input
                type={type || 'radio'}
                id={id}
                name={name}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                checked={checked}
                disabled={disabled}
            />
            <label
                htmlFor={id}
                className={[
                    checked ? 'checked' : null,
                    expandableDetails ? 'expandable-details' : null,
                ].join(' ')}
                onClick={(e) => {
                    if (onClick) onClick(e);

                    // absot radio click if toggling expandable details
                    const contains =
                        expandableDetailsToggleRef.current?.contains(
                            e.target as Node
                        );
                    if (expandableDetailsToggleRef.current && contains) {
                        e.preventDefault();
                    }
                }}
            >
                {image && (
                    <div className="image mb-12">
                        <Image
                            src={image}
                            unoptimized
                            loader={({ src, width, quality }) => src} // custom image loader so images come from imgix and not from next cdn
                            alt={imageAlt || ''}
                            layout="fill"
                            objectFit="cover"
                            objectPosition="center center"
                        />
                    </div>
                )}
                <div
                    className={`pr-32 label-container ${
                        !hasDescription() ? 'no-description' : ''
                    }`}
                >
                    {alternativeChoiceLayout ? (
                        <div className="alternative-layout">
                            <div className="col">
                                <div className={`label`}>
                                    {labelImage && (
                                        <span className="label-image">
                                            {labelImage}
                                        </span>
                                    )}
                                    {label}
                                </div>
                                {(description || theme === 'product') && (
                                    <Description className="description">
                                        {description}
                                    </Description>
                                )}
                            </div>
                            <div className="col">
                                {theme === 'product' && label && (
                                    <Button
                                        variant="tertiary"
                                        size="small"
                                        onClick={onReadMore}
                                    >
                                        Les mer
                                    </Button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className={`label`}>
                                {labelImage && (
                                    <span className="label-image">
                                        {labelImage}
                                    </span>
                                )}
                                {label}
                            </div>
                            {(description || theme === 'product') && (
                                <Description
                                    className="description"
                                    style={
                                        theme === 'product'
                                            ? { textAlign: 'center' }
                                            : undefined
                                    }
                                >
                                    {description}
                                    {theme === 'product' && label && (
                                        <div className="flex justify-center pt-16">
                                            <Button
                                                variant="tertiary"
                                                size="small"
                                                onClick={onReadMore}
                                            >
                                                Les mer
                                            </Button>
                                        </div>
                                    )}
                                </Description>
                            )}
                        </>
                    )}
                    {suffix && (
                        <div
                            className="suffix"
                            style={
                                suffixZIndex
                                    ? { zIndex: suffixZIndex }
                                    : undefined
                            }
                        >
                            {suffix}
                        </div>
                    )}
                    {expandableDetails && (
                        <div>
                            <div
                                ref={expandableDetailsToggleRef}
                                style={{ display: 'inline-flex' }}
                            >
                                <span
                                    className="expandable-deatils-label"
                                    onClick={() =>
                                        setDetailsExpanded(!detailsExpanded)
                                    }
                                >
                                    {Array.isArray(expandableDetailsLabel)
                                        ? detailsExpanded
                                            ? expandableDetailsLabel[1]
                                            : expandableDetailsLabel[0]
                                        : expandableDetailsLabel}
                                    <Icon
                                        icon={
                                            !detailsExpanded
                                                ? 'dropdown-small'
                                                : 'dropdown-small-open'
                                        }
                                        fill={colors.purple}
                                    />
                                </span>
                            </div>
                        </div>
                    )}
                    {detailsExpanded && (
                        <div style={{ paddingBottom: '6px' }}>
                            <Divider className="mt-16 mb-16" />
                            {expandableDetails}
                        </div>
                    )}
                    <Icon
                        icon={type === 'checkbox' ? 'checkbox' : 'radio'}
                        className=" icon radio"
                    />
                    <Icon
                        icon={
                            type === 'checkbox'
                                ? 'checkbox-active'
                                : 'radio-active'
                        }
                        className="icon radio-active"
                    />
                </div>
            </label>
            {statusLabel}
        </Wrapper>
    );
};

export default Radio;

const Wrapper = styled.div<{ zIndex: Props['zIndex'] }>`
    min-height: 24px;
    position: relative;

    ${(props) => (props.zIndex ? `z-index:${props.zIndex};` : ``)}

    input {
        position: absolute;
        z-index: -1;
        opacity: 0;
        visibility: hidden;
        -webkit-appearance: none;
    }

    label {
        cursor: pointer;
        display: flex;
        flex-direction: column;
        width: 100%;
        padding: 14px 16px;
        position: relative;
        border-radius: 12px;
        border: 3px solid transparent;
        transition: all 0.2s ease-in-out;
        ${effects.shadow};

        .image {
            width: 100%;
            height: 158px;
            border-radius: 4px;
            position: relative;
            overflow: hidden;
            @media (max-width: 767px) {
                height: 184px;
            }

            img {
                object-fit: contain;
            }
        }

        .label-container {
            position: relative;
            display: flex;
            flex-direction: column;
            flex: 1 0 auto;
            @media (max-width: 767px) {
                padding-right: 26px;
            }
        }

        &:hover {
            ${effects.shadowHover};
        }

        .label {
            font-size: 17px;
            font-family: 'gellix-semi';
            display: block;
            margin-bottom: 4px;
            .label-image {
                margin-right: 12px;
            }
        }

        .label-container > svg {
            position: absolute;
            top: 50%;
            right: 0;
            transform: translateY(-50%);
            transition: all 0.2s ease-in-out;
            &.radio-active {
                opacity: 0;
            }
        }

        .suffix {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            font-size: 17px;
            font-family: 'gellix-semi';
            right: 34px;
            z-index: 10;
        }

        .expandable-deatils-label {
            color: ${colors.purple};
            display: flex;
            align-items: center;
            font-family: 'gellix-semi';
            font-size: 17px;
            margin-top: 4px;
            user-select: none;
        }

        &.expandable-details {
            .suffix,
            .radio,
            .radio-active {
                transform: none;
                top: 0;
            }
        }
    }

    input:checked + label {
        border: 3px solid ${colors.purpleM1};
        .radio {
            opacity: 0;
        }
        .radio-active {
            opacity: 1;
        }
    }

    input:disabled + label {
        border: 3px solid white;
        .radio {
            opacity: 0;
        }
        .radio-active {
            opacity: 1;
        }
        &:hover {
            ${effects.shadow};
        }
    }

    &.theme-product {
        .icon {
            position: absolute;
            top: 31px;
            right: 12px;
            background: #fff;
            width: 38px;
            height: 38px;
            border-left: 12px solid #fff;
            border-bottom-left-radius: 24px;
            border-bottom: 12px solid #fff;
        }
        &.type-radio {
            .label-container {
                padding: 6px 4px;
                text-align: center;
                position: initial;
            }
            .label {
                font-size: 26px;
                font-family: 'gellix-semi';
            }
            label {
                padding: 14px 16px;
            }
        }
        &.type-checkbox {
            .label-container {
                padding: 6px 4px;
                position: initial;
            }
            .icon {
                border-bottom-left-radius: 16px;
            }
            .label {
                padding-right: 110px;
            }
            .description {
                position: relative;
                display: flex;
                > div {
                    padding: 0;
                    min-width: 98px;
                    margin-left: 12px;
                    button {
                        position: absolute;
                        bottom: 0;
                        right: 0;
                    }
                }
            }
            .label-container.no-description {
                .label {
                    padding-top: 12px;
                }
                .description > div button {
                    //bottom: -4px;
                }
            }
        }
    }
`;
